import React, { useMemo } from 'react';

import classnames from 'classnames';

import Image, { TRANSFORMATIONS } from 'components/uiLibrary/Image';
import LinkButton from 'components/uiLibrary/LinkButton';
import SeparatorList from 'components/uiLibrary/SeparatorList';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import Typography from 'components/uiLibrary/Typography';

import { getInitialsFromName } from 'utils';

import { COMPONENTS } from 'components/Globals/Analytics/constants';
import { getTicketsURL } from 'components/Productions/Display/TicketButton';

import { OUTBOUND_LINK_TYPES, TP, VALIDATION_STATUS } from 'constants/index';

import { getMediaSource } from 'utils/media';
import { convertTimeFormat } from 'utils/productions';

import { ENTITY_TYPES } from 'operabase-router/constants';

import classes from './ProductionComponents.module.scss';

export const DurationAndLangInfo = ({ production, size = 12, iconSize = 12, color = 'primary', styles }) => {
  const subHeaderTags = useMemo(
    () =>
      [
        {
          icon: 'clock',
          value: production?.duration ? convertTimeFormat(production.duration) : '',
        },
        {
          icon: 'world',
          value: production?.languages?.map(lang => lang?.name).join(', '),
        },
        {
          icon: 'closed_caption',
          value: production?.surtitles?.map(lang => lang?.name).join(', '),
        },
      ].reduce((accum, tag) => {
        if (tag.value) {
          accum.push(
            <Typography className={classes.tag} size={size} color={color}>
              <SpriteIcon icon={tag.icon} size={iconSize} className={classes.icon} />
              {tag.value}
            </Typography>,
          );
        }

        return accum;
      }, []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [production],
  );

  return (
    <SeparatorList
      data={subHeaderTags}
      separator=""
      styles={{
        root: classnames(classes.separatorListContainer, {
          [styles?.separatorListContainerBlock]: !!styles?.separatorListContainerBlock,
        }),
        item: classnames(classes.separatorItem, {
          [styles?.separatorItemBlock]: !!styles?.separatorItemBlock,
        }),
      }}
    />
  );
};

export const ProductionStatus = ({
  production,
  t,
  styles = {},
  size = 12,
  color = 'secondary',
  showText = true,
  italic = false,
}) => {
  const isRedMask = production?.validationStatus?.id === VALIDATION_STATUS.APPROVED;
  return (
    <Typography
      className={classnames(classes.redMask, styles.root, { [classes.italic]: italic })}
      color={color}
      size={size}
    >
      {isRedMask ? (
        <SpriteIcon icon="red_mask" className={classnames(classes.maskIcon, styles.maskIcon)} size={16} />
      ) : (
        <SpriteIcon icon="grey_mask" className={classnames(classes.maskIcon, styles.maskIcon)} size={16} />
      )}

      {showText &&
        (isRedMask ? t(`${TP}.FN_PRODUCTION_STATUS_TEXT`) : t(`${TP}.FN_PRODUCTION_INFORMATION_NOT_VERIFIED`))}
    </Typography>
  );
};

export const ProductionWebsite = ({
  production,
  styles = {},
  t,
  label,
  size = 12,
  color = 'secondary',
  type,
  showRightIcon = false,
  trackingData,
}) => {
  const ticketsUrl = useMemo(
    () => getTicketsURL(production, { prioritizeWebsiteURL: true, skipDateCheck: true }),
    [production],
  );
  const website = ticketsUrl || production?.website;

  if (!website) {
    return null;
  }

  return (
    <LinkButton
      href={website}
      isLink
      variant="text"
      styles={styles.button}
      external
      externalLinkType={OUTBOUND_LINK_TYPES.WEBSITE}
      rightIcon={showRightIcon ? <SpriteIcon icon="open_in_new" className={styles.icon} size={12} /> : undefined}
      trackingData={{
        ...trackingData,
        component: trackingData?.component || COMPONENTS.PRODUCTION_WEBSITE,
        meta: { website },
      }}
    >
      <Typography variant="span" size={size} color={color} className={styles.text} type={type}>
        {label || t(`${TP}.VISIT_WEBSITE`)}
      </Typography>
    </LinkButton>
  );
};

export const RenderEntityImage = ({ entity, size = 24, styles = {}, textSize = 12 }) => {
  const imageProps = {
    src: getMediaSource({ file: { urls: entity?.entity?.image } }, true),
    alt: entity?.entity?.name,
    width: size,
    transformations:
      entity?.entityType === ENTITY_TYPES.PROFILE ? TRANSFORMATIONS.PROFILE_THUMBNAIL : TRANSFORMATIONS.ORG_LOGO,
  };

  if (imageProps.src) {
    return (
      <div className={classnames(classes.entityImage, styles.root)}>
        <Image {...imageProps} disableNextImage />
      </div>
    );
  }

  return (
    <div className={classnames(classes.entityImage, styles.root)}>
      <Typography size={textSize} className={classnames(classes.imageLetters, styles.text)}>
        {getInitialsFromName(entity?.entity?.name)}
      </Typography>
    </div>
  );
};
